import { DefaultButton, DialogType, IButtonProps, IButtonStyles, Label, SearchBox, Stack } from "@fluentui/react"
import { Link } from "react-router-dom"
import { ReactNode, useRef } from "react"
import { MessageDialog } from "../../components/common/Dialog"
import { FileInfo } from "../../api/admin/models_admin"

interface AdminLabelProps {
    text: string
}
export const AdminLabel = ({text}: AdminLabelProps) => {
    return (<h2 style={{ 
        margin: 0,
        padding: "0.2rem 0.5rem",
        color: "#58535E",
        borderBottom: "0.3rem solid #0F6CBD",
        borderImage: "linear-gradient(to right,#0F6CBD,#FFF)",
        borderImageSlice: "1"
    }}>{text}</h2>)
}

interface LinkButtonProps {
    text: string
    link_to: string
    styles?: IButtonStyles
    disabled?: boolean
    external?: boolean
}
export const LinkButton = ({text, link_to, styles, disabled = false, external = false}: LinkButtonProps) => {
    if (disabled) {
        return (
            <span style={{ display: 'inline-block', width: 'fit-content' }}>
                <DefaultButton
                    text={text}
                    styles={styles}
                    disabled={true}
                />
            </span>
        )
    }
    
    if (external) {
        return (
            <a href={link_to} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', display: 'inline-block', padding: "0", width: 'fit-content' }}>
                <DefaultButton
                    text={text}
                    styles={styles}
                />
            </a>
        )
    }
    
    return (<Link to={link_to} style={{ textDecoration: 'none', display: 'inline-block', padding: "0", width: 'fit-content' }}>
        <DefaultButton
            text={text}
            styles={styles}
        />
    </Link>)
}

interface CellContentProps {
    children: ReactNode
    // 省略された全文をホバーで表示する文字列。childrenが文字列/数値なら自動で補われるので、
    // LinkやCheckboxなど要素を渡す場合にだけ明示する
    tooltip?: string
}
export const CellContent = ({children, tooltip}: CellContentProps) => {
    // 表示テキストが確定できる場合はツールチップを自動で補う
    const effectiveTooltip = tooltip
        ?? (typeof children === 'string' || typeof children === 'number' ? String(children) : undefined)

    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            fontSize: "11pt",
            fontWeight: 400,
            color: "black"
        }}>
            {
                // DetailsListのセルは既定で末尾を「…」で省略するが、上のflexコンテナを挟むと
                // それが効かなくなる。ツールチップで全文を読める場合のみ、この要素側で省略させる。
                // （要素を渡していてtooltip未指定の場合は全文を読む手段がないため、
                //   レイアウトを変えずに従来どおりそのまま描画する）
                effectiveTooltip === undefined ? children : (
                    <span
                        title={effectiveTooltip}
                        style={{
                            // flexアイテムをmin-content幅より小さく縮められるようにする
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {children}
                    </span>
                )
            }
        </div>
    )
}

export const calcMinWidth = (text: string, padding: number = 32): number => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return text.length * 14 + padding;
    ctx.font = '600 14px "Segoe UI", "Segoe UI Web (East Asian)", -apple-system, sans-serif';
    return Math.ceil(ctx.measureText(text).width) + padding;
};

export const formatDateTime = (dateString: string | null | undefined): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('ja-JP', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    }).replace(/\//g, '/').replace(',', '');
}

// 数字を数値として比較する自然順コレータ。「資料2」が「資料10」より前に並ぶ
export const naturalCollator = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: 'base'
});

// 一覧の絞り込み。空白区切りのキーワードをすべて含む要素だけを残す（大文字小文字は区別しない）。
// DetailsListを仮想化するとブラウザのページ内検索では見えていない行を拾えないため、
// 一覧側に絞り込みを用意する必要がある。
// toSearchTextは検索対象の文字列、または複数列を検索したい場合はその配列を返す。
export const filterByKeyword = <T,>(
    items: T[],
    keyword: string,
    toSearchText: (item: T) => string | (string | null | undefined)[]
): T[] => {
    const keywords = keyword.trim().toLowerCase().split(/\s+/).filter(k => k !== '');
    if (keywords.length === 0) return items;

    return items.filter(item => {
        const text = toSearchText(item);
        // 列をまたいだ偶然の一致を防ぐため、改行で連結してから検索する
        const target = (Array.isArray(text) ? text : [text])
            .filter((v): v is string => Boolean(v))
            .join('\n')
            .toLowerCase();
        return keywords.every(k => target.includes(k));
    });
};

interface ListFilterBoxProps {
    value: string
    onChange: (value: string) => void
    placeholder?: string
    // 件数を渡すと絞り込み結果を「n / m 件」で表示する
    filteredCount?: number
    totalCount?: number
    maxWidth?: number
}
export const ListFilterBox = (props: ListFilterBoxProps) => {
    const {value, onChange, placeholder = "キーワードで絞り込み", filteredCount, totalCount, maxWidth = 400} = props

    return (
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
            <SearchBox
                placeholder={placeholder}
                value={value}
                onChange={(_, newValue) => onChange(newValue ?? '')}
                onClear={() => onChange('')}
                styles={{ root: { width: '100%', maxWidth } }}
            />
            {filteredCount !== undefined && totalCount !== undefined && (
                <Label styles={{ root: { color: '#666', fontWeight: 400, whiteSpace: 'nowrap' } }}>
                    {filteredCount === totalCount ? `全${totalCount}件` : `${filteredCount} / ${totalCount}件`}
                </Label>
            )}
        </Stack>
    )
}

// 拡張子はFileInfo.typeに分かれて入っているため、画面に出す時は繋げる
export const displayFileName = (file: FileInfo): string =>
    `${file.filename}${file.type ? `.${file.type}` : ''}`;

export const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

interface DialogProps {
    isOpen: boolean
    title: string
    content: string
    onConfirm: () => void
    onDismiss: () => void
}

export const ErrorDialog = (props: Omit<DialogProps, 'title' | "onConfirm">) => {
    const {isOpen, content, onDismiss} = props
    return <ConfirmDialog isOpen={isOpen} title="エラー" content={content} onDismiss={onDismiss} />
}

export const ConfirmDialog = (props: Omit<DialogProps, "onConfirm">) => {
    const {isOpen, title, content, onDismiss} = props
    const messageDialogContentProps = {
        type: DialogType.normal,
        title: title,
        closeButtonAriaLabel: 'Close',
        subText: content,
    };
    return (
        <div style={{ position: 'absolute', pointerEvents: 'none' }}>
            <MessageDialog
                hidden={!isOpen}
                dialogContentProps={messageDialogContentProps}
                onConfirm={onDismiss}
                onDismiss={onDismiss}
                showCancelButton={false}
            />
        </div>
    )
}

export const YesNoDialog = (props: DialogProps) => {
    const {isOpen, title, content, onDismiss, onConfirm} = props
    const messageDialogContentProps = {
        type: DialogType.normal,
        title: title,
        closeButtonAriaLabel: 'Close',
        subText: content,
    };
    return (
        <div style={{ position: 'absolute', pointerEvents: 'none' }}>
            <MessageDialog
                hidden={!isOpen}
                dialogContentProps={messageDialogContentProps}
                onConfirm={onConfirm}
                onDismiss={onDismiss}
                showCancelButton={true}
            />
        </div>
    )
}

interface FilePickButtonProps extends IButtonProps {
    onFileChange: (files: FileList | null) => void
}
export const FilePickButton = (props: FilePickButtonProps) => {
    const {onFileChange, ...buttonProps} = props
    const fileInputRef = useRef<HTMLInputElement>(null)

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        e.stopPropagation()
        // ボタンが無効な間はドロップでも受け付けない
        if (props.disabled) return
        onFileChange(e.dataTransfer.files)
    }

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        e.stopPropagation()
    }

    return(<>
        <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => {
                onFileChange(e.target.files)
                e.target.value = ""
            }}
        />
        <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            style={{ display: 'inline-block' }}
        >
            <DefaultButton
                text="クリックしてファイルを追加"
                iconProps={{ iconName: 'Attach' }}
                styles={{ root: { width: 'fit-content' } }}
                {...buttonProps}
                onClick={
                    (e) => {
                        fileInputRef.current?.click()
                        props.onClick?.(e)
                    }
                }
            />
        </div>
    </>)
}